import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../utils/AuthContext';
import {
  isJulyFourthLocalDevReplay,
  isJulyFourthPreviewMode,
  julyFourthEventId,
  markCelebrationSeen,
  shouldShowJulyFourthCelebration,
  shouldSkipCelebrationPersistence,
} from '../../utils/seasonalCelebrations';
import JulyFourthCelebration from './JulyFourthCelebration';

/** Brief delay so the celebration doesn't collide with the initial route paint. */
const SHOW_DELAY_MS = 700;

/**
 * One-time seasonal overlays for internal employees. Persists dismissal on the
 * user doc via `seenCelebrationIds` (same pattern as release announcements).
 */
const SeasonalCelebrationGate: React.FC = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [eventId, setEventId] = useState<string | null>(null);
  const markedRef = useRef(false);
  const sessionSeenRef = useRef<string[]>([]);

  useEffect(() => {
    if (!user) return;
    const id = julyFourthEventId();
    if (!shouldSkipCelebrationPersistence() && sessionSeenRef.current.includes(id)) return;
    if (!shouldShowJulyFourthCelebration(user)) {
      return;
    }

    const delay = isJulyFourthPreviewMode() || isJulyFourthLocalDevReplay() ? 120 : SHOW_DELAY_MS;
    const timer = window.setTimeout(() => {
      setEventId(id);
      setVisible(true);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [user]);

  const persistSeen = useCallback(
    (id: string) => {
      if (shouldSkipCelebrationPersistence()) return;
      if (markedRef.current || !user) return;
      markedRef.current = true;
      sessionSeenRef.current = sessionSeenRef.current.includes(id)
        ? sessionSeenRef.current
        : [...sessionSeenRef.current, id];
      markCelebrationSeen(user.id, id).catch(err => {
        console.warn('Failed to record celebration seen:', err);
      });
    },
    [user],
  );

  const handleComplete = useCallback(() => {
    if (eventId) persistSeen(eventId);
    setVisible(false);
  }, [eventId, persistSeen]);

  if (!visible) return null;

  return <JulyFourthCelebration onComplete={handleComplete} />;
};

export default SeasonalCelebrationGate;
